import Service from "../service";

/**
 * Admin Diagnostic FAQ API wrapper (server: /diagnostic-search).
 *
 * Same convention as globalSearchApi.js: the server envelope
 * `{ status: 200 | 403, data, message }` (configs.success_status / configs.error_status)
 * is returned untouched so the caller can check `status` and read `message` on failure.
 *
 * Admin-only on the server (see server/controller/diagnosticSearch.js) - a
 * non-Admin caller gets `status: 403` back. The client must still gate the
 * call itself (see useGlobalSearch.js) so a non-Admin viewer never even fires
 * this request; the server gate is defense-in-depth, not the only gate.
 *
 * Expected `data` shape (null when the query doesn't match a recognized
 * attendance diagnostic intent, or when no employee could be resolved):
 * {
 *   intent: "attendance_absent_despite_punch",
 *   employee: { _id, name },
 *   date: "YYYY-MM-DD",
 *   answer: "plain-language answer text",
 *   evidence: { ...raw facts used }
 * }
 */

// Same client-side give-up as globalSearchApi.js's search() - this call is a
// best-effort, parallel (Promise.all) companion to the main global search
// call in useGlobalSearch.js, and must never let a hung/slow backend here
// hold up or kill that main search. See globalSearchApi.js for the full
// reasoning on why the timeout lives here rather than in Service.makeAPICall.
const DIAGNOSTIC_TIMEOUT_MS = 10000; // 10s - same budget as the main search call

const diagnosticSearchApi = {
  /** Admin-only diagnostic search across the ATTENDANCE intent catalog. params: { q } */
  search: async (q) => {
    // Never rejects (same reasoning as globalSearchApi.search) - a timeout or
    // network failure here resolves to a plain 403-shaped envelope, which
    // useGlobalSearch's existing "not status 200 -> no Answer group, never an
    // error" handling already treats as a no-op, never propagating up through
    // the shared Promise.all and never affecting the main search half.
    const apiCallPromise = Service.makeAPICall({ methodName: Service.getMethod, api_url: "/diagnostic-search", body: { q } }).then(
      (response) => response?.data || { status: 403, data: null, message: "No response from server" }
    );
    let timer;
    const timeoutPromise = new Promise((resolve) => {
      timer = setTimeout(
        () => resolve({ status: 403, data: null, message: "Diagnostic search timed out" }),
        DIAGNOSTIC_TIMEOUT_MS
      );
    });
    const result = await Promise.race([apiCallPromise, timeoutPromise]);
    clearTimeout(timer);
    return result;
  },
};

export default diagnosticSearchApi;
